import React, {
  ForwardedRef,
  forwardRef,
  ReactNode,
  RefObject,
  useRef,
} from 'react';
import {
  AriaDatePickerProps,
  DateValue,
  useDatePicker,
} from 'react-aria/useDatePicker';

import clsx from 'clsx';
import styles from '@danske/sapphire-css/components/dateField/dateField.module.css';
import { Calendar as CalendarIcon } from '@danske/sapphire-icons/react';
import { DateInput } from './DateInput';
import { Calendar, CalendarProps } from '../../Calendar';
import { DateFieldButton } from './DateFieldButton';
import { PressResponder } from 'react-aria/private/interactions/PressResponder';
import { useDialog } from 'react-aria/useDialog';
import { SapphireStyleProps, useThemeCheck } from '../../utils';
import { Field } from '../../Field';
import { Label } from '../../Label';
import { Popover } from '../../Popover';
import { View } from '../../View';
import { tokens } from '@danske/sapphire-css/themes';
import { Icon } from '../../Icon';
import { PredefinedDate } from '../../Calendar';
import {
  DatePickerStateOptions,
  useDatePickerState,
} from './useDatePickerState';
import {
  BaseComponentKeys,
  DateFieldBaseKeys,
  GlobalDomAttributes,
  OverlayTriggerPropsKeys,
  ValueBasePropsKeys,
} from '../../utils/types';

export type DateFieldProps<T extends DateValue, P extends string> = Pick<
  AriaDatePickerProps<T>,
  | DateFieldBaseKeys
  | ValueBasePropsKeys
  | 'name'
  | BaseComponentKeys
  | OverlayTriggerPropsKeys
> &
  SapphireStyleProps &
  GlobalDomAttributes &
  Pick<CalendarProps, 'granularity'> & {
    /**
     * Handler that is called when the value changes.
     */
    onChange?: DatePickerStateOptions<DateValue, P>['onChange'];
    /**
     * Whether week numbers are visible as a first column
     */
    showWeekNumbers?: boolean;
    /**
     * Whether the input should render as having an error and an error message.
     * This also sets the appropriate `aria` attributes on the input.
     *
     * A `boolean` sets the error state. Can be paired with the "note" prop with Field.Note variant="error"
     * A `ReactNode` sets the error state with an additional error message.
     *
     * **An error message is strongly recommended because an error should
     * always have an explanation about how to fix it.**
     */
    error?: boolean | ReactNode;
    /**
     * A note to show below the input.
     * If the input has an error message, this note will be replaced by that.
     * Use {@Link Field.Note} to render the note.
     */
    note?: ReactNode;
    /**
     * A HelpButton to render next to the label.
     */
    contextualHelp?: ReactNode;
    /**
     * If it should hide the "clear" button when input is not empty.
     *
     * @default false
     */
    noClearButton?: boolean;
    /**
     * If it should hide the "calendar" button
     *
     * @default false
     */
    noCalendarButton?: boolean;
    /**
     * @default 'lg'
     */
    size?: 'lg' | 'md';
    /**
     * To visually indicate if this field is required or optional.
     * @default false
     */
    necessityIndicator?: boolean;
    /**
     * Places the label either above (default) or on the side of the control.
     * @default 'above'
     */
    labelPlacement?: 'side' | 'above';
    /**
     * A set of predefined dates that the user can more conveniently select.
     */
    predefinedDates?: Record<P, PredefinedDate>;
    /**
     * Aligns the text inside the input fields without affecting the
     * positioning of the label or icon of the field.
     * @default false
     */
    alignInputRight?: boolean;
  };

function _DateField<T extends DateValue, P extends string>(
  {
    error,
    note,
    contextualHelp,
    noClearButton = false,
    showWeekNumbers,
    size = 'lg',
    labelPlacement = 'above',
    necessityIndicator = false,
    noCalendarButton = false,
    predefinedDates,
    granularity,
    onKeyDown, // We will intercept this
    alignInputRight = false,
    ...otherProps
  }: DateFieldProps<T, P>,
  ref: ForwardedRef<HTMLDivElement>
) {
  useThemeCheck();

  const groupRef = useRef(null);
  const dialogRef = useRef(null);

  const { dialogProps } = useDialog({}, dialogRef);

  const state = useDatePickerState({
    ...otherProps,
    isInvalid: error ? true : undefined,
  });

  const {
    groupProps,
    labelProps,
    fieldProps,
    buttonProps,
    dialogProps: dialogPropsFromDatePicker,
    calendarProps,
    descriptionProps,
    errorMessageProps,
  } = useDatePicker(
    {
      ...otherProps,
      description: note,
    },
    state,
    groupRef
  );

  const isMedium = size === 'md';

  /**
   * Intercept key down presses in capture phase before React Aria consumes it.
   * This is useful to catch specific key presses before the propagation is stopped in React Aria layer.
   */
  const handleKeyDownCapture = (e: React.KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.({
      ...e,
      continuePropagation: () => {},
    });
  };

  return (
    <Field
      {...otherProps}
      noShrink
      isDisabled={otherProps.isDisabled}
      size={size}
      ref={ref}
      labelPlacement={labelPlacement}
    >
      <Field.Context
        descriptionProps={error ? errorMessageProps : descriptionProps}
      >
        {otherProps.label && (
          <Field.Label>
            <Label
              {...labelProps}
              size={size}
              necessityIndicator={
                otherProps.isRequired && necessityIndicator
                  ? 'required'
                  : !otherProps.isRequired && necessityIndicator
                  ? 'optional'
                  : undefined
              }
              contextualHelp={contextualHelp}
            >
              {otherProps.label}
            </Label>
          </Field.Label>
        )}
        <Field.Control>
          <div
            {...groupProps}
            ref={groupRef}
            onKeyDownCapture={handleKeyDownCapture}
            className={clsx(styles['sapphire-date-field'], {
              [styles['sapphire-date-field--error']]: state.isInvalid,
              [styles['sapphire-date-field--no-calendar']]: noCalendarButton,
              [styles['sapphire-date-field--no-clear-button']]: noClearButton,
              [styles['sapphire-date-field--md']]: isMedium,
              [styles['sapphire-date-field--align-right']]: alignInputRight,
            })}
          >
            {!alignInputRight && !noCalendarButton ? (
              <PressResponder {...buttonProps} isPressed={state.isOpen}>
                <DateFieldButton
                  isDisabled={otherProps.isDisabled || otherProps.isReadOnly}
                >
                  <Icon size={isMedium ? 'sm' : 'md'}>
                    <CalendarIcon />
                  </Icon>
                </DateFieldButton>
              </PressResponder>
            ) : null}
            <DateInput
              {...fieldProps}
              noClearButton={noClearButton}
              size={size}
              granularity={granularity}
              alignInputRight={alignInputRight}
            />
            {alignInputRight && !noCalendarButton ? (
              <PressResponder {...buttonProps} isPressed={state.isOpen}>
                <DateFieldButton
                  isDisabled={otherProps.isDisabled || otherProps.isReadOnly}
                >
                  <Icon size={isMedium ? 'sm' : 'md'}>
                    <CalendarIcon />
                  </Icon>
                </DateFieldButton>
              </PressResponder>
            ) : null}
          </div>
        </Field.Control>
        {(!!error && typeof error !== 'boolean') || note ? (
          <Field.Footer>
            {!!error && typeof error !== 'boolean' ? (
              <Field.Note variant="error">{error}</Field.Note>
            ) : note ? (
              note
            ) : null}
          </Field.Footer>
        ) : null}
        {state.isOpen && (
          <Popover
            restoreFocus
            state={state}
            triggerRef={groupRef}
            placement="bottom start"
          >
            <div
              ref={dialogRef}
              {...dialogProps}
              {...dialogPropsFromDatePicker}
            >
              <View padding={tokens.size.spacingLg}>
                <Calendar
                  {...calendarProps}
                  size="md"
                  defaultFocusedValue={state.dateValue}
                  showWeekNumbers={showWeekNumbers}
                  predefinedDates={predefinedDates}
                  granularity={granularity}
                  showFixedWeeks
                />
              </View>
            </div>
          </Popover>
        )}
      </Field.Context>
    </Field>
  );
}

/**
 * A date field allows users to enter or edit date values using the keyboard
 * and/or mouse.
 */
export const DateField = forwardRef(_DateField) as <
  T extends DateValue,
  P extends string
>(
  props: DateFieldProps<T, P> & { ref?: RefObject<HTMLDivElement> }
) => React.ReactElement;
